import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema.gen';
import { env } from '@/lib/env';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Render free/cheap instances cold-start: the first request after an idle period
 * can take ~30–60s while the server spins up. We give every request a strict
 * 15s ceiling (via AbortController) and transparently retry ONCE on a timeout or
 * network error so a transient cold-start hiccup recovers without surfacing to
 * the user. Non-network failures (a real 4xx/5xx response) are NOT retried here —
 * those flow through react-query's own retry policy / error handling.
 */
const REQUEST_TIMEOUT_MS = 15_000;
const MAX_RETRIES = 1;

export async function fetchWithTimeoutAndRetry(
  input: Request | string | URL,
  init?: RequestInit,
): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);
    try {
      return await fetch(input, { ...init, signal: controller.signal });
    } catch (err) {
      // Only retry transport failures: our own timeout abort, or a network
      // error (fetch rejects with a TypeError). A caller-initiated abort
      // (init.signal) must propagate immediately, not be retried.
      const callerAborted = init?.signal?.aborted ?? false;
      const isTimeout = timedOut;
      const isNetworkError = err instanceof TypeError;
      if (callerAborted || (!isTimeout && !isNetworkError)) throw err;
      lastError = err;
    } finally {
      clearTimeout(timer);
    }
  }
  throw lastError;
}

// Latch so a burst of concurrent 401s (several Command Center queries failing at
// once when a token expires) triggers only a single redirect. The hard
// navigation below reloads the bundle, which resets this for the next session.
let sessionExpiryRedirecting = false;

const authMiddleware: Middleware = {
  onRequest({ request }) {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    return request;
  },
  onResponse({ response }) {
    if (response.status === 401) {
      const { isAuthenticated, clearSession } = useAuthStore.getState();
      clearSession();
      // A 401 against a session that *was* live means the token died out from
      // under the user (expired / revoked). Force them out with a HARD document
      // navigation rather than leaving the soft <Navigate> in RequireAuth to do
      // it: from the Command Center a client-side route change tears down the
      // live Leaflet map mid-commit, which can throw and leave the route
      // half-changed — the panel stays on screen even though the session is gone
      // (exactly the "logged out via 401 but still seeing Command Center" bug).
      // A full reload hands teardown to the browser so logout can't get stuck.
      // The `isAuthenticated` guard skips this for a 401 from the login request
      // itself (bad credentials), so that form keeps its inline error.
      if (isAuthenticated && !sessionExpiryRedirecting) {
        sessionExpiryRedirecting = true;
        window.location.replace('/login');
      }
    }
    return response;
  },
};

export const apiClient = createClient<paths>({
  baseUrl: env.VITE_API_URL,
  credentials: 'include',
  fetch: fetchWithTimeoutAndRetry,
});

apiClient.use(authMiddleware);
